import { makeAutoObservable, reaction } from 'mobx';
import RootStore from 'stores/RootStore';
import ServiceStore from 'stores/ServiceStore';
import testData from './testData.json'; // Sample data for testing

/**
 * @interface QCItem
 * @description Interface for Quality Control items
 */
export interface QCItem {
  id: string;
  elementId: string;
  elementType: string;
  productionDate: string; // ISO Date string preferably
  inspectionDate?: string;
  inspector?: string;
  status: 'Pending' | 'Passed' | 'Failed' | 'Rework';
  dimensionCheck?: boolean;
  reinforcementCheck?: boolean;
  surfaceFinishCheck?: boolean;
  notes?: string;
  // ... other relevant fields
}

/**
 * @interface QCFilterCriteria
 * @description Interface for QC filtering criteria
 */
export interface QCFilterCriteria {
  status?: 'Pending' | 'Passed' | 'Failed' | 'Rework';
  elementType?: string;
  elementId?: string;
  inspectionDateFrom?: string;
  inspectionDateTo?: string;
  productionDateFrom?: string;
  productionDateTo?: string;
  inspector?: string;
  [key: string]: string | undefined;
}

/**
 * @class QualityControlStore
 * @description Store for managing quality control data and operations
 */
class QualityControlStore {
  rootStore: RootStore;
  loading: boolean = false;
  qcItems: QCItem[] = [];
  selectedItemId: string | null = null;
  filterCriteria: QCFilterCriteria = {};
  disposers: Array<() => void> = [];

  /**
   * @constructor
   * @description Initialize the store and setup reactions
   */
  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
      disposers: false,
    });

    this.rootStore = rootStore;

    // Setup reactions for side effects
    this.disposers.push(
      reaction(
        () => this.selectedItem,
        (item) => {
          if (item) {
            // Additional logic when selected item changes
            console.log('Selected QC item changed:', item.elementId);
          }
        }
      )
    );
  }

  /**
   * @method dispose
   * @description Clean up reactions when store is no longer needed
   */
  dispose = (): void => {
    this.disposers.forEach((disposer) => disposer());
  };

  /**
   * @method setLoading
   * @description Update loading state
   */
  setLoading = (state: boolean): void => {
    this.loading = state;
  };

  /**
   * @method setSelectedItemId
   * @description Set the ID of the selected QC item
   */
  setSelectedItemId = (id: string | null): void => {
    this.selectedItemId = id;
  };

  /**
   * @method fetchItems
   * @description Fetch QC items with optional filtering criteria
   */
  fetchItems = async (
    criteria: Partial<QCFilterCriteria> = {}
  ): Promise<QCItem[]> => {
    this.setLoading(true);
    try {
      // Merge with existing filter criteria
      this.filterCriteria = { ...this.filterCriteria, ...criteria };

      // Use a service store for API calls, ensuring it's typed for QCItem
      const qcServiceStore = this.rootStore.qcServiceStore as ServiceStore;
      const result = await qcServiceStore.find({
        query: this.filterCriteria,
      }); // Assuming FindResponse returns data compatible with QCItem[]

      if (result?.data) {
        this.qcItems = result.data as unknown as QCItem[];
      }

      return this.qcItems;
    } catch (error: unknown) {
      console.error('Error fetching QC items:', error);
      return [];
    } finally {
      this.setLoading(false);
    }
  };

  /**
   * @method updateItemStatus
   * @description Update the status of a QC item
   */
  updateItemStatus = async (
    itemId: number | string,
    newStatus: 'Passed' | 'Failed' | 'Rework'
  ): Promise<boolean> => {
    try {
      const qcServiceStore = this.rootStore.qcServiceStore as ServiceStore;
      await qcServiceStore.patch(itemId as number, { status: newStatus });

      // Update local state optimistically
      this.qcItems = this.qcItems.map((item) =>
        item.id === itemId ? { ...item, status: newStatus } : item
      );

      return true;
    } catch (error: unknown) {
      console.error('Error updating QC item status:', error);
      return false;
    }
  };

  /**
   * @computed selectedItem
   * @description Get the currently selected QC item
   */
  get selectedItem(): QCItem | null {
    if (!this.selectedItemId) return null;
    return this.qcItems.find((item) => item.id === this.selectedItemId) || null;
  }

  /**
   * @private _entities
   * @description Sample data for QC items (internal storage)
   */
  private _entities: QCItem[] = testData as QCItem[];

  /**
   * @computed entities
   * @description Sample data for QC items (accessed as computed)
   * Note: This is static sample data. In a real application,
   * this would likely be fetched or derived from other state.
   */
  get entities(): QCItem[] {
    // If qcItems is populated from fetch, return that, otherwise return sample data.
    // Adjust this logic based on whether you want sample data as fallback or initial state.
    // For now, just returning the static sample data.
    // If you intend to replace this with fetched data, consider removing this computed
    // or adjusting the logic in fetchItems and the initial state of qcItems.
    return this._entities;
    // Or, if you want fetched data to override sample data:
    // return this.qcItems.length > 0 ? this.qcItems : this._entities;
  }

  /**
   * @computed filteredItems
   * @description Get items filtered by current criteria
   */
  get filteredItems(): QCItem[] {
    // Apply any additional filtering logic here
    return this.entities;
  }

  /**
   * @computed title
   * @description Get the title for the QC view
   */
  get title(): string {
    return 'Precast Quality Control Dashboard';
  }

  /**
   * @computed isLoading
   * @description Check if any loading operations are in progress
   */
  get isLoading(): boolean {
    const qcServiceStore = this.rootStore.qcServiceStore as ServiceStore;
    return (
      this.loading || // Use the local loading state
      (qcServiceStore &&
        (qcServiceStore.isGetPending || // Corrected property name
          qcServiceStore.isFindPending || // Corrected property name
          qcServiceStore.isCreatePending || // Corrected property name
          qcServiceStore.isUpdatePending || // Corrected property name
          qcServiceStore.isPatchPending || // Corrected property name
          qcServiceStore.isRemovePending)) // Corrected property name
    );
  }
}

export default QualityControlStore;
